import {
  ArrowRight,
  Bell,
  Check,
  CheckCheck,
  CheckSquare,
  MessageSquarePlus,
  RefreshCw,
} from 'lucide-react'
import type { RoomNotification } from '../types'
import type { useRoomNotifications } from '../useRoomNotifications'

export function NotificationsPanel({
  inbox,
  busy,
  onOpen,
}: {
  inbox: ReturnType<typeof useRoomNotifications>
  busy: boolean
  onOpen: (item: RoomNotification) => void
}) {
  const disabled = busy || inbox.mutating
  return (
    <section aria-label="Your notifications">
      <header className="room-section-heading">
        <div>
          <h3>A little nudge, then back to the story.</h3>
          <p>Assignments, task changes, and feedback updates for you.</p>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Refresh notifications"
          disabled={inbox.loading}
          onClick={() => void inbox.refresh()}
        >
          <RefreshCw size={15} className={inbox.loading ? 'spin' : ''} />
        </button>
      </header>
      {inbox.error && (
        <div className="room-error" role="alert">
          {inbox.error}
          <button
            type="button"
            disabled={inbox.loading}
            onClick={() => void inbox.refresh()}
          >
            Try again
          </button>
        </div>
      )}
      <div className="notification-controls">
        <span role="status">
          {inbox.loaded
            ? `${inbox.unreadCount} unread`
            : 'Checking your updates…'}
        </span>
        <button
          type="button"
          className="button"
          disabled={disabled || !inbox.unreadCount || inbox.loading}
          onClick={() => void inbox.markRead()}
        >
          <CheckCheck size={14} /> Mark all read
        </button>
      </div>
      <ol className="notification-list">
        {inbox.items.map((item) => (
          <li
            className={'notification-card' + (!item.readAt ? ' is-unread' : '')}
            key={item.id}
          >
            <span className="notification-symbol" aria-hidden="true">
              {item.kind === 'task' ? (
                <CheckSquare size={18} />
              ) : (
                <MessageSquarePlus size={18} />
              )}
            </span>
            <div>
              <div className="notification-meta">
                <span>
                  {item.actor.name} · {item.action}
                </span>
                {!item.readAt && (
                  <strong className="notification-unread">Unread</strong>
                )}
              </div>
              <button
                type="button"
                className="notification-title"
                disabled={disabled}
                onClick={() => onOpen(item)}
              >
                {item.title}
                <ArrowRight size={14} />
              </button>
              {item.message && <p className="notification-message">{item.message}</p>}
              <time dateTime={item.createdAt}>
                {new Date(item.createdAt).toLocaleString()}
              </time>
            </div>
            {!item.readAt && (
              <button
                type="button"
                className="icon-button"
                disabled={disabled}
                aria-label={'Mark as read: ' + item.title}
                title="Mark as read"
                onClick={() => void inbox.markRead(item.id)}
              >
                <Check size={16} />
              </button>
            )}
          </li>
        ))}
      </ol>
      {inbox.loaded && !inbox.items.length && !inbox.error && (
        <div className="notification-empty">
          <Bell size={28} />
          <h4>You’re all caught up.</h4>
          <p>
            New assignments and updates from the other writer will appear here.
          </p>
        </div>
      )}
      {inbox.next !== null && (
        <button
          type="button"
          className="button notification-more"
          disabled={inbox.loading || disabled}
          onClick={() => void inbox.loadMore()}
        >
          {inbox.loading ? 'Loading…' : 'Load earlier notifications'}
        </button>
      )}
      <p className="notification-footnote">
        Reading an update here keeps it read on your account. Your personal
        tasks stay private.
      </p>
    </section>
  )
}
